<template>
  <Transition mode="out-in">
    <div
      v-if="!props.skeleton && hasData"
      key="chart--data"
      class="rf-prompt-data--chart-wrapper -mt-px box-content flex w-full max-w-full min-w-px flex-col pt-6"
    >
      <div class="rf-prompt-data--height relative">
        <canvas ref="chart" class="rf-prompt-data--height absolute inset-0"></canvas>
      </div>
      <div
        v-if="!props.noLegend"
        class="rf-prompt-chart--legend mt-2 box-border inline-flex justify-between gap-2 overflow-x-auto overflow-y-visible"
      >
        <slot v-if="$slots.default" />
        <span v-else></span>
        <div v-show="showLegend" class="rf-prompt-chart--legend-container h-6"></div>
      </div>
    </div>
    <div
      v-else-if="!props.skeleton && !hasData"
      key="chart--empty"
      class="box-content flex py-2"
      style="height: calc(var(--rf-prompt-data--height, 249px) + 8px)"
    >
      <span class="text-action-buttons text-beige-50 m-auto"> There is no data. </span>
    </div>
    <div
      v-else
      key="chart--load"
      class="box-content flex flex-col pt-6"
      :class="{ 'pb-8': props.noLegend }"
    >
      <div class="flex items-start">
        <div class="rf-prompt-chart--chart flex w-full items-start gap-5">
          <div class="flex grow-0 flex-col items-end justify-end self-end">
            <RfBaseSkeleton
              v-for="i in 5"
              :key="i"
              width="30"
              height="16"
              style="margin-bottom: calc(var(--rf-prompt-data--height, 249px) / 6 - 16px)"
            />
            <RfBaseSkeleton width="20" height="16" />
          </div>
          <div class="flex w-full min-w-px flex-col">
            <div
              class="bg-stripes before:border-b-grey-1 mt-2 flex grow gap-2 before:border-b-2 before:border-solid"
              style="height: calc(var(--rf-prompt-data--height, 249px) - 40px)"
            >
              <div
                v-for="i in 6"
                :key="i"
                class="flex h-full flex-1 flex-col items-center justify-end gap-3"
                style="margin-top: 26px"
              >
                <RfBaseSkeleton
                  height="100"
                  dynamicHeight
                  rangeHeight="50"
                  class="w-full! max-w-24! rounded-b-none!"
                />
                <RfBaseSkeleton width="40" height="16" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!props.noLegend" class="flex h-6 items-center justify-end gap-6 pt-10">
        <div v-for="i in 6" :key="i" class="inline-flex items-center gap-2">
          <RfBaseSkeleton height="24" width="24" />
          <RfBaseSkeleton height="10" width="50" dynamic />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, onUnmounted, ref, watchEffect } from "vue";
import { useBarChart } from "@/utils/composables/useBarChart";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { newChartHasData } from "@/utils/metricsHelpers";

const props = defineProps({
  skeleton: { type: Boolean, default: false },
  data: { type: Array, default: () => [] },
  noLegend: { type: Boolean, default: false },
  hasData: { type: [Boolean, undefined], default: undefined },
  selectedChartKey: { type: Number, default: undefined },
});

const chart = ref(null);
const destroyChart = ref(() => null);

const showLegend = computed(() => props.data?.length > 1 && !props.selectedChartKey);
const hasData = computed(() =>
  typeof props.hasData === "boolean" ? props.hasData : newChartHasData(props.data),
);

watchEffect(() => {
  if (!props.skeleton && hasData.value && chart.value)
    destroyChart.value = useBarChart(
      chart,
      props.selectedChartKey ? [props.data[props.selectedChartKey - 1]] : props.data,
    );
});

onUnmounted(destroyChart?.value);

defineExpose({ hasData });
</script>

<style scoped>
.rf-prompt-data--height {
  height: var(--rf-prompt-data--height, 249px);
  max-height: var(--rf-prompt-data--height, 249px);
  min-height: var(--rf-prompt-data--height, 249px);
}

.bg-stripes {
  position: relative;
  background: repeating-linear-gradient(
    0deg,
    #cdcbc2 #cdcbc2 transparent 1px,
    transparent calc(var(--rf-prompt-data--height, 249px) / 6)
  );
  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      90deg,
      white 4px,
      white 8px,
      transparent 8px,
      transparent 13px
    );
  }
}
</style>
